import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Save, Store } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { tenantsApi } from "@/lib/api";
import { apiErrorMessage } from "@/lib/api/errors";
import { BUSINESS_TYPES, SALE_MODES, businessTypeLabel, isShop, saleModeLabel } from "@/lib/api/types";
import type { BusinessType, SaleMode, TenantDto } from "@/lib/api/types";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { StaffTab } from "@/pages/Tenants/tabs/StaffTab";
import { MenuTab } from "@/pages/Tenants/tabs/MenuTab";
import { OrdersTab } from "@/pages/Tenants/tabs/OrdersTab";
import { SalesTab } from "@/pages/Tenants/tabs/SalesTab";
import { ShopSalesTab } from "@/pages/Tenants/tabs/ShopSalesTab";

type Tab = "staff" | "menu" | "orders" | "sales";

const TABS: { key: Tab; label: string; restaurantOnly?: boolean }[] = [
  { key: "staff", label: "Staff" },
  { key: "menu", label: "Menu", restaurantOnly: true },
  { key: "orders", label: "Orders", restaurantOnly: true },
  { key: "sales", label: "Sales" },
];

export default function TenantDetailPage() {
  const { id } = useParams();
  const tenantId = Number(id);
  const [tab, setTab] = useState<Tab>("staff");

  const tenant = useQuery({
    queryKey: ["tenants", tenantId],
    queryFn: () => tenantsApi.get(tenantId),
    enabled: Number.isFinite(tenantId),
  });

  if (!Number.isFinite(tenantId)) {
    return <EmptyState icon={Store} title="Business not found" description="That link doesn't look right." />;
  }

  return (
    <div>
      <Link
        to="/tenants"
        className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> All businesses
      </Link>

      <PageHeader
        title={tenant.data?.name ?? "Business"}
        description={
          isShop(tenant.data?.businessType)
            ? "Manage this business's details, staff and sales"
            : "Manage this business's details, staff, menu, orders, and sales"
        }
        actions={
          tenant.data && (
            <div className="flex items-center gap-2">
              <Badge variant="outline">{businessTypeLabel(tenant.data.businessType)}</Badge>
              {tenant.data.businessType !== "RESTAURANT" && (
                <Badge variant="outline">{saleModeLabel(tenant.data.saleMode)}</Badge>
              )}
              <Badge variant={tenant.data.active ? "success" : "destructive"}>
                {tenant.data.active ? "Active" : "Inactive"}
              </Badge>
            </div>
          )
        }
      />

      {tenant.isLoading && <Skeleton className="h-40 w-full" />}
      {tenant.isError && (
        <EmptyState icon={Store} title="Couldn't load business" description={apiErrorMessage(tenant.error)} />
      )}

      {tenant.data && (
        <>
          <TenantInfoCard tenant={tenant.data} />

          {/* Shops have no menu or restaurant orders; their sales come from the shop layer. */}
          <div className="-mx-3 mb-4 mt-6 overflow-x-auto px-3 sm:mx-0 sm:px-0">
          <div className="inline-flex rounded-[var(--radius-pill)] border border-border bg-card p-1">
            {TABS.filter((t) => !(t.restaurantOnly && isShop(tenant.data.businessType))).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "whitespace-nowrap rounded-[var(--radius-pill)] px-4 py-1.5 text-sm font-semibold transition-colors",
                  tab === t.key
                    ? "bg-gradient-sunset text-primary-foreground shadow-glow"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          </div>

          {tab === "staff" && (
            <StaffTab tenantId={tenantId} tenantName={tenant.data.name} businessType={tenant.data.businessType} />
          )}
          {tab === "menu" && !isShop(tenant.data.businessType) && <MenuTab tenantId={tenantId} />}
          {tab === "orders" && !isShop(tenant.data.businessType) && <OrdersTab tenantId={tenantId} />}
          {tab === "sales" &&
            (isShop(tenant.data.businessType) ? <ShopSalesTab tenantId={tenantId} /> : <SalesTab tenantId={tenantId} />)}
        </>
      )}
    </div>
  );
}

function TenantInfoCard({ tenant }: { tenant: TenantDto }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(tenant.name);
  const [address, setAddress] = useState(tenant.address ?? "");
  const [phoneNumber, setPhoneNumber] = useState(tenant.phoneNumber ?? "");
  const [businessType, setBusinessType] = useState<BusinessType>(tenant.businessType ?? "RESTAURANT");
  const [saleMode, setSaleMode] = useState<SaleMode>(tenant.saleMode ?? "RETAIL");

  // Re-seed the form when a fresh tenant payload arrives (e.g. after save).
  useEffect(() => {
    setName(tenant.name);
    setAddress(tenant.address ?? "");
    setPhoneNumber(tenant.phoneNumber ?? "");
    setBusinessType(tenant.businessType ?? "RESTAURANT");
    setSaleMode(tenant.saleMode ?? "RETAIL");
  }, [tenant]);

  const dirty =
    name.trim() !== tenant.name ||
    address.trim() !== (tenant.address ?? "") ||
    phoneNumber.trim() !== (tenant.phoneNumber ?? "") ||
    businessType !== (tenant.businessType ?? "RESTAURANT") ||
    saleMode !== (tenant.saleMode ?? "RETAIL");

  const save = useMutation({
    mutationFn: () =>
      tenantsApi.update(tenant.id, {
        name: name.trim(),
        address: address.trim(),
        phoneNumber: phoneNumber.trim(),
        businessType,
        saleMode,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast({ title: "Business updated", variant: "success" });
    },
    onError: (err) =>
      toast({ title: "Couldn't save changes", description: apiErrorMessage(err), variant: "destructive" }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Business details</CardTitle>
        <CardDescription>Type, name and contact information shown across the platform</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto]"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label>Business type</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value as BusinessType)}
            >
              {BUSINESS_TYPES.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </div>
          {businessType !== "RESTAURANT" && (
            <div className="space-y-1.5">
              <Label>Sells</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={saleMode}
                onChange={(e) => setSaleMode(e.target.value as SaleMode)}
              >
                {SALE_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="—" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="—" />
          </div>
          <Button type="submit" disabled={save.isPending || !dirty || !name.trim()}>
            <Save className="h-4 w-4" />
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
